/* Booking-source badge, 16x16 inline SVG.
 *
 * Inline rather than an image file on purpose: no asset pipeline, no network request per row, and
 * the rack renders dozens of these at once. TopBarStatus.jsx is the existing inline-SVG precedent
 * in this kit.
 *
 * The KEY IS DECIDED ON THE SERVER, by FrontOffice::BookingSourceBadge, which defers to
 * Property::Analytics::SourceCanonicalizer. Do not add matching logic here — eZee sends free text
 * ("CTrip" for Trip.com, lower-case "booking.com"), and a second matcher in JS would drift from the
 * Channel Mix chart about the same booking. This component only draws what it is handed.
 *
 * These are original letter/shape marks, NOT the operators' own logos.
 */
const GLYPHS = {
  agoda: (
    <>
      <rect width="16" height="16" rx="3.5" fill="#5C2D91" />
      <g transform="translate(3.86 11.90)">
        <path
          d="M5.72 -1.42H2.57L2.07 0.00H0.05L2.94 -7.80H5.34L8.23 0.00H6.21ZM3.07 -2.87H5.21L4.14 -5.97Z"
          fill="#FFFFFF"
        />
      </g>
    </>
  ),
  booking: (
    <>
      <rect width="16" height="16" rx="3.5" fill="#003580" />
      <g transform="translate(3.81 11.90)">
        <path
          d="M4.11 -4.78Q4.58 -4.78 4.83 -4.99Q5.07 -5.20 5.07 -5.61Q5.07 -6.01 4.83 -6.22Q4.58 -6.43 4.11 -6.43H2.99V-4.78ZM4.17 -1.37Q4.78 -1.37 5.09 -1.62Q5.39 -1.88 5.39 -2.40Q5.39 -2.90 5.09 -3.16Q4.79 -3.41 4.17 -3.41H2.99V-1.37ZM6.04 -4.17Q6.69 -3.99 7.05 -3.48Q7.40 -2.97 7.40 -2.24Q7.40 -1.11 6.64 -0.55Q5.88 0.00 4.32 0.00H0.98V-7.80H4.00Q5.63 -7.80 6.36 -7.31Q7.08 -6.82 7.08 -5.74Q7.08 -5.17 6.82 -4.77Q6.55 -4.37 6.04 -4.17Z"
          fill="#FFFFFF"
        />
      </g>
    </>
  ),
  trip: (
    <>
      <rect width="16" height="16" rx="3.5" fill="#287DFA" />
      <g transform="translate(4.35 11.90)">
        <path
          d="M0.05 -7.80H7.24V-6.28H4.65V0.00H2.64V-6.28H0.05Z"
          fill="#FFFFFF"
        />
      </g>
    </>
  ),
  expedia: (
    <>
      <rect width="16" height="16" rx="3.5" fill="#FFC94D" />
      <g transform="translate(4.25 11.90)">
        <path
          d="M0.98 -7.80H6.41V-6.28H2.99V-4.83H6.21V-3.31H2.99V-1.52H6.53V0.00H0.98Z"
          fill="#00355F"
        />
      </g>
    </>
  ),
  /* Deliberately NOT the letter A. As supplied, Airbnb reused Agoda's glyph byte-for-byte and was
   * told apart by background colour alone — and Agoda is half of every booking here. A ring reads
   * as a different shape at 16px even when the colour does not register. */
  airbnb: (
    <>
      <rect width="16" height="16" rx="3.5" fill="#FF5A5F" />
      <path
        d="M8 3.4c-1.9 0-3.4 1.5-3.4 3.4 0 2.2 2.5 4.9 3.1 5.5a0.45 0.45 0 0 0 0.62 0c0.58-0.6 3.08-3.3 3.08-5.5C11.4 4.9 9.9 3.4 8 3.4Zm0 4.9a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3Z"
        fill="#FFFFFF"
      />
    </>
  ),
  /* Trip, Traveloka and Tiket all begin with T, so these two carry shapes rather than a second and
   * third letter T that would be unreadable next to each other. */
  traveloka: (
    <>
      <rect width="16" height="16" rx="3.5" fill="#0770CD" />
      <path
        d="M3.6 8.9 12.2 3.7a0.5 0.5 0 0 1 0.73 0.62L9.6 12.6a0.5 0.5 0 0 1-0.93-0.05L7.7 9.4 4.6 8.4a0.5 0.5 0 0 1-0.05-0.93Z"
        fill="#FFFFFF"
      />
    </>
  ),
  tiket: (
    <>
      <rect width="16" height="16" rx="3.5" fill="#0064D2" />
      <path
        d="M3.6 6.2c0-0.5 0.4-0.9 0.9-0.9h7c0.5 0 0.9 0.4 0.9 0.9v0.7a1.1 1.1 0 0 0 0 2.2v0.7c0 0.5-0.4 0.9-0.9 0.9h-7a0.9 0.9 0 0 1-0.9-0.9v-0.7a1.1 1.1 0 0 0 0-2.2Z"
        fill="#FFFFFF"
      />
    </>
  ),
  /* Our own desk, so it wears our own mark: the H of the HEBE wordmark, in the brand's charcoal and
   * gold (_kit/theme.js). The logo's framed goddess head is the more distinctive part of the mark,
   * but it is a fine line drawing — rendered into 16px it collapses into grey mush, which is worse
   * than no badge. The letterform survives, and among A / B / T / E it reads immediately as "ours".
   *
   * Weight is corrected on purpose: the real wordmark is a Didone with hairline serifs and a thin
   * crossbar, and both disappear at this size, so the serifs and bar are thickened to survive. */
  walkin: (
    <>
      <rect width="16" height="16" rx="3.5" fill="#1a1a1a" />
      <g fill="#c9a86c">
        <rect x="3.85" y="4.2" width="2.8" height="0.75" />
        <rect x="3.85" y="11.05" width="2.8" height="0.75" />
        <rect x="4.4" y="4.2" width="1.7" height="7.6" />
        <rect x="9.35" y="4.2" width="2.8" height="0.75" />
        <rect x="9.35" y="11.05" width="2.8" height="0.75" />
        <rect x="9.9" y="4.2" width="1.7" height="7.6" />
        <rect x="6.1" y="7.35" width="3.8" height="1.3" />
      </g>
    </>
  ),
  hotel: (
    <>
      <rect width="16" height="16" rx="3.5" fill="#64748B" />
      <g fill="#FFFFFF">
        <rect x="3.3" y="4.6" width="1.8" height="7" rx="0.6" />
        <rect x="3.3" y="8.4" width="9.4" height="3.2" rx="0.9" />
        <rect x="5.9" y="6.5" width="2.9" height="2.1" rx="0.7" />
      </g>
    </>
  ),
};

/* Brand names are not translated. `walkin` and `hotel` are, because they name a concept rather than
 * a company — their labels come from the caller via the `label` prop. */
const BRAND_LABELS = {
  agoda: "Agoda",
  booking: "Booking.com",
  trip: "Trip.com",
  expedia: "Expedia",
  airbnb: "Airbnb",
  traveloka: "Traveloka",
  tiket: "Tiket.com",
};

const BookingSourceIcon = (props) => {
  const key = GLYPHS[props.sourceKey] ? props.sourceKey : "hotel";
  const label = BRAND_LABELS[key] || props.label || key;
  const size = props.size || 16;

  return (
    <svg
      viewBox="0 0 16 16"
      width={size}
      height={size}
      role="img"
      aria-label={label}
      shapeRendering="geometricPrecision"
      style={{ flex: "0 0 auto", display: "block" }}
    >
      <title>{label}</title>
      {GLYPHS[key]}
    </svg>
  );
};

export default BookingSourceIcon;
